import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface TestModeContextType {
  isTestMode: boolean;
  toggleTestMode: () => void;
  setTestMode: (value: boolean) => void;
}

const TestModeContext = createContext<TestModeContextType | undefined>(undefined);

interface TestModeProviderProps {
  children: ReactNode;
}

export function TestModeProvider({ children }: TestModeProviderProps) {
  const [isTestMode, setIsTestMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('testMode');
    return saved === 'true';
  });

  const toggleTestMode = useCallback(() => {
    setIsTestMode(prev => {
      const newValue = !prev;
      localStorage.setItem('testMode', String(newValue));
      return newValue;
    });
  }, []);

  const setTestMode = useCallback((value: boolean) => {
    setIsTestMode(value);
    localStorage.setItem('testMode', String(value));
  }, []);

  return (
    <TestModeContext.Provider value={{ isTestMode, toggleTestMode, setTestMode }}>
      {children}
    </TestModeContext.Provider>
  );
}

export function useTestMode(): TestModeContextType {
  const context = useContext(TestModeContext);
  if (context === undefined) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return context;
}

export default TestModeContext;
